import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { useI18n } from "@/lib/i18n";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Key, Check, Loader2, ShieldCheck } from "lucide-react";
import api from "@/lib/api";

export default function AppSumoPage() {
  const navigate = useNavigate();
  const { login: authLogin } = useAuth();
  const { t } = useI18n();
  const [step, setStep] = useState(1);
  const [code, setCode] = useState("");
  const [keyInfo, setKeyInfo] = useState(null);
  const [form, setForm] = useState({ email: "", password: "", name: "", company_name: "" });
  const [validating, setValidating] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const validateKey = async () => {
    setValidating(true);
    try {
      const res = await api.post("/appsumo/validate-key", { code: code.trim() });
      if (res.data.valid) {
        setKeyInfo(res.data);
        setStep(2);
        toast.success(`Key geldig! Plan: ${res.data.plan_name}`);
      } else {
        toast.error(res.data.error || "Ongeldige key");
      }
    } catch { toast.error(t("common.error")); }
    finally { setValidating(false); }
  };

  const handleRedeem = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const res = await api.post("/appsumo/redeem", { code: code.trim(), ...form });
      localStorage.setItem("sp_token", res.data.token);
      localStorage.setItem("sp_user", JSON.stringify(res.data.user));
      toast.success(res.data.message);
      navigate("/dashboard");
      window.location.reload();
    } catch (err) {
      toast.error(err.response?.data?.detail || t("common.error"));
    } finally { setSubmitting(false); }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4 py-12" data-testid="appsumo-page">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-2">
            <img src="/logo.jpg" alt="Smartpriced" className="h-10 w-auto" />
          </Link>
          <div className="mt-4">
            <Badge className="bg-orange-500 text-white text-sm px-4 py-1">AppSumo Deal</Badge>
          </div>
        </div>

        {step === 1 ? (
          <Card className="shadow-xl shadow-slate-200/50 border-slate-100">
            <CardHeader className="text-center pb-2">
              <CardTitle className="text-xl flex items-center justify-center gap-2"><Key className="w-5 h-5 text-orange-500" />{t("appsumo.title")}</CardTitle>
              <CardDescription>{t("appsumo.subtitle")}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-5">
              <div>
                <Label>{t("appsumo.key_label")} *</Label>
                <Input value={code} onChange={e => setCode(e.target.value.toUpperCase())} placeholder="SP-XXXXX-XXXXX-XXXXX" className="mt-1.5 font-mono-data text-center tracking-wider" data-testid="appsumo-key-input" />
              </div>
              <Button onClick={validateKey} disabled={!code.trim() || validating} className="w-full bg-orange-500 hover:bg-orange-600 h-11" data-testid="validate-key-btn">
                {validating ? <Loader2 className="w-4 h-4 animate-spin" /> : t("appsumo.validate")}
              </Button>
              <p className="text-center text-xs text-slate-400">{t("appsumo.key_hint")}</p>
            </CardContent>
          </Card>
        ) : (
          <Card className="shadow-xl shadow-slate-200/50 border-slate-100">
            <CardHeader className="text-center pb-2">
              <div className="flex items-center justify-center gap-2 mb-2">
                <ShieldCheck className="w-5 h-5 text-emerald-500" />
                <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200">{keyInfo?.plan_name} - {keyInfo?.tier}</Badge>
              </div>
              <CardTitle className="text-xl">{t("appsumo.create_account")}</CardTitle>
              <CardDescription>{keyInfo?.product_limit?.toLocaleString()} {t("appsumo.products_included")}</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleRedeem} className="space-y-4">
                <div><Label>{t("appsumo.name_label")} *</Label><Input value={form.name} onChange={e => setForm({...form, name: e.target.value})} required className="mt-1" data-testid="appsumo-name" /></div>
                <div><Label>{t("appsumo.email_label")} *</Label><Input type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} required className="mt-1" data-testid="appsumo-email" /></div>
                <div><Label>{t("appsumo.password_label")} *</Label><Input type="password" value={form.password} onChange={e => setForm({...form, password: e.target.value})} required minLength={8} className="mt-1" data-testid="appsumo-password" /></div>
                <div><Label>{t("appsumo.company_label")}</Label><Input value={form.company_name} onChange={e => setForm({...form, company_name: e.target.value})} className="mt-1" /></div>
                <Button type="submit" disabled={submitting} className="w-full bg-orange-500 hover:bg-orange-600 h-11" data-testid="redeem-btn">
                  {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Check className="w-4 h-4 mr-2" />{t("appsumo.activate")}</>}
                </Button>
              </form>
              <p className="text-center text-sm text-slate-500 mt-4">
                {t("appsumo.has_account")} <Link to="/login" className="text-blue-600 font-medium">{t("appsumo.login_link")}</Link>
              </p>
            </CardContent>
          </Card>
        )}
        <div className="mt-6 text-center">
          <Link to="/" className="text-sm text-blue-600 hover:text-blue-700">{t("appsumo.back_home")}</Link>
        </div>
      </div>
    </div>
  );
}
